// Formato dos downloads dos gráficos Highcharts (menu burger: Download CSV /
// Download XLS). Regras da mesa:
//   - decimal SEMPRE com PONTO (nunca vírgula) — os números abrem íntegros
//     em Excel/Sheets sem o parser de localidade embaralhar as casas;
//   - delimitador vírgula no CSV (par natural do decimal com ponto);
//   - data dd/mm/aaaa — sem a "hora fantasma" que o eixo datetime exporta
//     por default ("YYYY-MM-DD hh:mm:ss").
//
// `CSV_EXPORTING` carrega as DUAS metades da regra: a config de CSV e a
// redefinição do item "Download XLS" do menu. Aplique nas `options.exporting`
// de cada gráfico — é o caminho confiável, porque não depende de ordem de
// carga de módulo. `patchHighchartsExports()` continua no bootstrap como rede
// de segurança global, mas um gráfico que passa `CSV_EXPORTING` está correto
// mesmo que o patch global não alcance a instância dele.
//
// O app tem TRÊS instâncias do Highcharts: a da raiz (dependência direta,
// 9.x), e duas dentro de @jgp-er-dev/charts (11.x) — `highcharts.js` e
// `highstock.js`, bundles UMD independentes. É o HIGHSTOCK que serve os
// RegularChart/TimeChart e o menu de export.

// Ruído de ponto flutuante: 0.3230000000000001 vira 0.323, e 2.5749999999999997
// vira 2.575. Doze dígitos significativos preservam qualquer número real que a
// tela produz (taxas, spreads, durations) e cortam só a sujeira de divisões
// feitas no front (média móvel, base 100, ponderações).
const limparRuidoFloat = (n: number): number => Number(n.toPrecision(12))

// Template do XLS — o do módulo export-data do Highcharts, com DUAS correções:
//
// 1. As classes. O Highcharts 11 marca as células como `highcharts-number` /
//    `highcharts-text`, mas o `<style>` original ainda mira `.number` / `.text`
//    (nomes da v9). Resultado: nenhum `mso-number-format` pegava, e o Excel
//    caía no parser de localidade — era isto que embaralhava o XLS enquanto o
//    CSV saía certo.
// 2. `mso-number-format:"General"` nos números em vez de `"0.00"`: preserva as
//    casas decimais que o dado tem (spread com 3 casas não vira 2).
const XLS_HEAD =
  '<html xmlns:o="urn:schemas-microsoft-com:office:office" ' +
  'xmlns:x="urn:schemas-microsoft-com:office:excel" ' +
  'xmlns="http://www.w3.org/TR/REC-html40">' +
  '<head><!--[if gte mso 9]><xml><x:ExcelWorkbook>' +
  '<x:ExcelWorksheets><x:ExcelWorksheet>' +
  '<x:Name>Ark1</x:Name>' +
  '<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>' +
  '</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>' +
  '</xml><![endif]-->' +
  '<style>td{border:none;font-family: Calibri, sans-serif;} ' +
  '.highcharts-number{mso-number-format:"General";} ' +
  '.highcharts-text{mso-number-format:"\\@";}</style>' +
  '<meta name=ProgId content=Excel.Sheet>' +
  '<meta charset=UTF-8>' +
  '</head><body>'

// Anota a tabela com os atributos que o Excel lê ANTES de olhar o texto da
// célula: `x:num` entrega o valor numérico cru (imune ao locale — em Excel
// pt-BR "0.323" sozinho vira texto ou 323) e `x:str` trava as células de
// texto para datas dd/mm/aaaa não serem reinterpretadas.
export const prepararTabelaXLS = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html')

  doc.querySelectorAll('.highcharts-number').forEach((celula) => {
    const bruto = Number(celula.textContent)
    if (!Number.isFinite(bruto)) return
    const limpo = limparRuidoFloat(bruto)
    celula.textContent = String(limpo)
    celula.setAttribute('x:num', String(limpo))
  })

  doc.querySelectorAll('.highcharts-text').forEach((celula) => {
    celula.setAttribute('x:str', '')
  })

  return doc.body.innerHTML
}

// Shape mínimo do que precisamos de um gráfico (evita depender dos tipos de
// duas versões diferentes do Highcharts ao mesmo tempo).
interface GraficoExportavel {
  getTable: (useLocalDecimalPoint?: boolean) => string
  getFilename: () => string
}

// Baixa uma `<table>` JÁ anotada (x:num / x:str, classes `highcharts-number` /
// `highcharts-text`) dentro do template corrigido. Serve também às tabelas
// que não são gráfico (ex.: export da HDS), para o XLS sair igual em toda a casa.
export const baixarTabelaXLS = (tabela: string, nomeArquivo: string): void => {
  const template = XLS_HEAD + tabela + '</body></html>'
  const a = document.createElement('a')
  a.href =
    'data:application/vnd.ms-excel;base64,' +
    window.btoa(unescape(encodeURIComponent(template)))
  a.download = `${nomeArquivo}.xls`
  document.body.appendChild(a)
  a.click()
  a.remove()
}

// `getTable(false)` = decimal com PONTO em vez do decimal do locale do
// navegador (o downloadXLS original usa `getTable(true)`, e num navegador
// pt-BR isso já entrega vírgula). O ponto é o que `x:num` exige.
export const baixarXLS = (grafico: GraficoExportavel): void => {
  baixarTabelaXLS(
    prepararTabelaXLS(grafico.getTable(false)),
    grafico.getFilename()
  )
}

// Highcharts nomeia cada coluna do arquivo com o nome da série — "Spread" e
// "Duration" saem como dois números sem unidade, e quem abre o arquivo não tem
// como saber que um é % e o outro é ano. Anexamos a unidade que a própria série
// já declara no `tooltip.valueSuffix`, então a regra vale para todo gráfico sem
// configuração extra: "Spread (%)" e "Duration (anos)".
//
// Devolver `false` = usa o default do Highcharts. A string serve tanto ao CSV
// quanto à tabela do XLS (lá o multi-level header cai no fallback da string).
interface SerieOuEixo {
  name?: string
  coll?: string
  dateTime?: boolean
  options?: {
    title?: { text?: string }
    tooltip?: { valueSuffix?: string }
  }
}

export const formatarCabecalhoColuna = (
  item?: SerieOuEixo,
  _key?: string,
  keyLength?: number
): string | false => {
  if (!item) return false

  // Eixo (a coluna do X). Se o eixo tem título, o default já o usa; num eixo
  // de data sem título o default é o literal "DateTime".
  if (item.coll !== 'series') {
    if (item.options?.title?.text) return false
    return item.dateTime ? 'Data' : false
  }

  // Séries com mais de um valor por ponto (faixas, OHLC): o default já
  // distingue as colunas pelo nome da chave — não mexemos.
  if (keyLength && keyLength > 1) return false

  const unidade = item.options?.tooltip?.valueSuffix?.trim()
  if (!unidade || !item.name) return false
  // Evita "Spread (%) (%)" se o nome da série já trouxer a unidade.
  if (item.name.includes(unidade)) return false
  return `${item.name} (${unidade})`
}

export const CSV_EXPORTING = {
  csv: {
    columnHeaderFormatter: formatarCabecalhoColuna,
    dateFormat: '%d/%m/%Y',
    decimalPoint: '.',
    itemDelimiter: ',',
  },
  // Substitui o item nativo do menu. Highcharts chama `onclick` com `this`
  // apontando pro gráfico.
  menuItemDefinitions: {
    downloadXLS: {
      textKey: 'downloadXLS',
      onclick(this: GraficoExportavel) {
        baixarXLS(this)
      },
    },
  },
}

// Shape mínimo de uma instância Highcharts.
interface HighchartsLike {
  setOptions: (options: object) => void
  Chart?: {
    prototype: {
      downloadXLS?: () => void
      getTable?: (useLocalDecimalPoint?: boolean) => string
      getFilename?: () => string
    }
  }
}

const patchInstance = (H: HighchartsLike): void => {
  // CSV + menu: config global da instância (options de gráfico fazem merge
  // por cima).
  H.setOptions({ exporting: CSV_EXPORTING })
  // XLS: sobrescreve também o protótipo, para gráficos que não passam
  // `CSV_EXPORTING` nas próprias options. Guard: se o módulo export-data não
  // está carregado nessa instância, não há menu — nada a fazer.
  const proto = H.Chart?.prototype
  if (!proto || typeof proto.getTable !== 'function') return
  proto.downloadXLS = function (this: GraficoExportavel) {
    baixarXLS(this)
  }
}

// Aplica o formato em TODAS as instâncias do Highcharts do bundle. Chamar uma
// vez no bootstrap, depois dos imports (os módulos exporting/export-data já
// definiram os protótipos; criado nenhum gráfico ainda).
// `unknown` + cast interno: as duas instâncias têm versões (e tipos) de
// Highcharts diferentes — a checagem estrutural exata brigaria com ambas.
export const patchHighchartsExports = (raiz: unknown): void => {
  patchInstance(raiz as HighchartsLike)
  // São TRÊS instâncias, não duas — e a que desenha os RegularChart é a
  // última desta lista. `@jgp-er-dev/charts/dist/index.js` faz
  // `require('highcharts/highstock')` e aplica exporting/export-data NELA;
  // `highcharts.js` e `highstock.js` são bundles UMD independentes, com
  // `Chart.prototype` e `defaultOptions` próprios (verificado: require.resolve
  // devolve arquivos diferentes e os módulos não são o mesmo objeto). Até
  // 28/09/2026 o patch mirava só o `highcharts.js`, então era no-op para todo
  // gráfico da casa: o CSV saía com o decimal do locale e a data com hora.
  //
  // Os require abaixo são LITERAIS de propósito. Com o caminho numa variável o
  // webpack emite "Critical dependency: the request of a dependency is an
  // expression" e resolve por context module: no bundle o require lança, o
  // catch engole, e o patch global vira no-op em silêncio — exatamente a falha
  // que este código existe para evitar. Em jest passa dos dois jeitos, então o
  // teste não pega; só o build denuncia.
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('@jgp-er-dev/charts/node_modules/highcharts')
    patchInstance((mod?.default ?? mod) as HighchartsLike)
  } catch {
    // instância ausente (npm deduplicou, ou versão sem este bundle)
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('@jgp-er-dev/charts/node_modules/highcharts/highstock')
    patchInstance((mod?.default ?? mod) as HighchartsLike)
  } catch {
    // instância ausente (npm deduplicou, ou versão sem este bundle)
  }
}
